import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or retrieve a persistent device ID
function getDeviceId() {
  let id = localStorage.getItem("advanta_device_id");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("advanta_device_id", id);
  }
  return id;
}

// Set device ID as a global default header - ensures it's always sent
axios.defaults.headers.common["X-Device-Id"] = getDeviceId();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("advanta_token"));

  // Set up axios interceptors
  useEffect(() => {
    // Add device_id header to all requests
    const reqInterceptor = axios.interceptors.request.use((config) => {
      config.headers["X-Device-Id"] = getDeviceId();
      return config;
    });

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we get a 401 Unauthorized, clear the token and redirect to login
        if (error.response?.status === 401) {
          console.log("Token expired or invalid, logging out...");
          localStorage.removeItem("advanta_token");
          setToken(null);
          setUser(null);
          // Public pages should never bounce to /login on a stale-token 401 —
          // they may be opened by an unauthenticated venue staff member or visitor.
          const path = window.location.pathname;
          const publicPrefixes = [
            '/login', '/register', '/reset-password', '/team-invite',
            '/scan', '/partner-signup', '/affiliate-signup', '/validate',
            '/businesses', '/discounts', '/forgot-password', '/privacy', '/terms',
          ];
          const onPublicPage = path === '/' || publicPrefixes.some((p) => path === p || path.startsWith(p + '/'));
          if (!onPublicPage) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}`, "X-Device-Id": getDeviceId() }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Auth error:", error);
      // Clear invalid token
      localStorage.removeItem("advanta_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, remember_me = false) => {
    // Clear any existing token first
    localStorage.removeItem("advanta_token");
    setToken(null);
    setUser(null);

    const device_id = getDeviceId();
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me, device_id });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("advanta_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    // Clear any existing token first
    localStorage.removeItem("advanta_token");
    
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("advanta_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("advanta_token");
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      token, 
      login, 
      register, 
      logout, 
      getAuthHeader,
      refreshUser: fetchUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
